
(() => {
  const COOKIE_KEY_POPUP_VIEWS = 'persona_popup_views';
  const COOKIE_KEY_CUSTOMER_HASH = 'persona_customer_hash';
  const POPUPS_URL = 'https://api.weshoes.get-persona.com/api/public/companies/cmh8v7mdw002412i688eahxx3/popups';
  // Shopify renders our custom pixel in <iframe id="web-pixel-sandbox-CUSTOM-<pixelId>-LAX-..." sandbox="allow-scripts allow-forms">.
  // Without allow-same-origin the frame has an opaque origin, so its messages arrive with event.origin === 'null'.
  const PIXEL_FRAME_ID_PREFIX = 'web-pixel-sandbox-CUSTOM-152535405-';
  const PIXEL_FRAME_ORIGIN = 'null';

  let popups = [];
  let popupsLoadedForHash;
  let shownPopups = new Set();
  const GLOBAL_POPUP_STYLE_ID = 'persona-popup-fixed-style';

  function ensureGlobalPopupStyles() {
    if (document.getElementById(GLOBAL_POPUP_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = GLOBAL_POPUP_STYLE_ID;
    style.textContent = '.u-popup-container { position: fixed !important; }';
    document.head.appendChild(style);
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  function setCookie(name, value, days = 365) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  function deleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  function getObjectCookie(name) {
    const raw = getCookie(name);
    if (!raw) return {};
    try {
      const obj = JSON.parse(decodeURIComponent(raw));
      return typeof obj === 'object' && !Array.isArray(obj) ? obj : {};
    } catch {
      return {};
    }
  }

  function setObjectCookie(name, obj) {
    setCookie(name, JSON.stringify(obj));
  }

  function getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'TABLET';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'MOBILE';
    }
    return 'DESKTOP';
  }

  /**
   * Convert wildcard pattern to regex
   * Supports * as wildcard (e.g., /products/* or *sale*)
   */
  function wildcardToRegex(pattern, { contains = false } = {}) {
    // Escape special regex characters except *
    const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    // Replace * with .*
    const regexPattern = escaped.replace(/\*/g, '.*');
    const finalPattern = contains ? regexPattern : `^${regexPattern}$`;
    return new RegExp(finalPattern, 'i');
  }

  /**
   * Check if current URL matches URL conditions
   * Returns true if ALL conditions pass (AND logic)
   */
  function checkUrlConditions(urlConditions) {
    if (!urlConditions || !Array.isArray(urlConditions) || urlConditions.length === 0) {
      return true; // No conditions = always pass
    }

    const path = window.location.pathname;
    const search = window.location.search || '';

    let decodedPath = path;
    try {
      decodedPath = decodeURIComponent(path);
    } catch {
      // Ignore decode errors; fall back to raw path
    }

    const urlCandidates = [
      path + search,
      decodedPath + search
    ];

    for (const condition of urlConditions) {
      const { type, value } = condition;
      if (!value) continue; // Skip empty conditions

      let matches = false;

      if (type === 'EQUALS') {
        matches = urlCandidates.some(u => u === value);
      } else if (type === 'NOT_EQUALS') {
        matches = urlCandidates.every(u => u !== value);
      } else if (type === 'CONTAINS') {
        const regex = wildcardToRegex(value, { contains: true });
        matches = urlCandidates.some(u => regex.test(u));
      } else if (type === 'NOT_CONTAINS') {
        const regex = wildcardToRegex(value, { contains: true });
        matches = urlCandidates.every(u => !regex.test(u));
      }

      // If any condition fails, return false (AND logic)
      if (!matches) {
        return false;
      }
    }

    return true; // All conditions passed
  }

  /**
   * Listen for messages from sandboxed pixel
   */
  function isFromPixelFrame(event) {
    if (event.origin !== PIXEL_FRAME_ORIGIN || !event.source) return false;
    const frame = document.querySelector(`iframe[id^="${PIXEL_FRAME_ID_PREFIX}"]`);
    if (!frame || frame.contentWindow !== event.source) return false;
    try {
      return new URL(frame.src).origin === window.location.origin;
    } catch {
      return false;
    }
  }

  window.addEventListener('message', (event) => {
    if (!isFromPixelFrame(event)) return;
    const msg = event.data;
    if (!msg || typeof msg !== 'object' || msg.source !== 'persona-pixel') return;
    const data = msg.data || {};

    switch (msg.type) {
      case 'PIXEL_READY':
        handlePixelReady();
        break;

      case 'SHOPIFY_EVENT':
        handleShopifyEvent(data.eventName, data.eventData);
        break;

      case 'CART_ITEM_ADDED':
      case 'CART_ITEM_REMOVED':
        handleCartEvent(data);
        break;
    }
  });

  // The pixel may have identified the visitor (and set the hash cookie) after the first load
  function reloadPopupsIfHashChanged() {
    if (getCookie(COOKIE_KEY_CUSTOMER_HASH) !== popupsLoadedForHash) loadPopups();
  }

  function handlePixelReady() {
    reloadPopupsIfHashChanged();
  }

  /**
   * Popup HTML is fetched here, straight from the Persona API, and never taken from a postMessage:
   * it is rendered with innerHTML and its scripts run, so it must come from the same source as this file.
   */
  async function loadPopups() {
    const hash = getCookie(COOKIE_KEY_CUSTOMER_HASH);
    popupsLoadedForHash = hash;
    const url = hash ? `${POPUPS_URL}?hash=${encodeURIComponent(decodeURIComponent(hash))}` : POPUPS_URL;
    try {
      const response = await fetch(url, { credentials: 'omit' });
      if (!response.ok) return;
      const loaded = await response.json();
      if (Array.isArray(loaded) && loaded.length > 0) handlePopupsLoaded(loaded);
    } catch (error) {
      console.error('[Persona DOM] Loading popups failed:', error);
    }
  }

  function handlePopupsLoaded(loadedPopups) {
    popups = loadedPopups || [];

    popups.forEach(popup => {
      if (!popup.isEligible) return;
      setupPopupTriggers(popup);
    });
  }

  function setupPopupTriggers(popup) {
    const triggers = popup.frontendTriggers || {};

    // Check timesPerVisitor limit (from either frontendTriggers or displayFrequency)
    const timesPerVisitor = triggers.timesPerVisitor || popup.displayFrequency?.timesPerVisitor;

    if (timesPerVisitor) {
      const viewsObj = getObjectCookie(COOKIE_KEY_POPUP_VIEWS);
      const viewCount = viewsObj[popup.id] || 0;

      if (viewCount >= timesPerVisitor) {
        return;
      }
    }

    if (triggers.deviceTypes && Array.isArray(triggers.deviceTypes)) {
      const deviceType = getDeviceType();
      if (!triggers.deviceTypes.includes(deviceType)) {
        return;
      }
    }

    // Check URL conditions
    if (!checkUrlConditions(triggers.urlConditions)) {
      return;
    }

    if (triggers.exitIntent) {
      setupExitIntent(popup);
    }

    if (triggers.afterSeconds) {
      setTimeout(() => {
        showPopup(popup);
      }, triggers.afterSeconds * 1000);
    }
    if (triggers.afterMinutes) {
      setTimeout(() => {
        showPopup(popup);
      }, triggers.afterMinutes * 60 * 1000);
    }

    if (triggers.scrollPercent) {
      setupScrollTrigger(popup, triggers.scrollPercent);
    }

    if (triggers.hoverSelector) {
      setupHoverTrigger(popup, triggers.hoverSelector);
    }

    if (triggers.clickSelector) {
      setupClickTrigger(popup, triggers.clickSelector);
    }

    // If no display triggers are specified (or only URL conditions), show immediately
    const hasTimingTriggers = triggers.exitIntent || triggers.afterSeconds || triggers.afterMinutes ||
                              triggers.scrollPercent || triggers.hoverSelector || triggers.clickSelector ||
                              (triggers.customEvents && triggers.customEvents.length > 0);

    if (!hasTimingTriggers) {
      // No timing/interaction triggers ‚Äî show immediately
      // (device type and URL conditions already passed above)
      showPopup(popup);
    }

  }

  function setupExitIntent(popup) {
    let hasTriggered = false;

    document.addEventListener('mouseout', (e) => {
      if (hasTriggered || shownPopups.has(popup.id)) return;
      if (e.clientY < 50 && e.relatedTarget === null) {
        hasTriggered = true;
        showPopup(popup);
      }
    });
  }

  function setupScrollTrigger(popup, scrollPercent) {
    let hasTriggered = false;

    window.addEventListener('scroll', () => {
      if (hasTriggered || shownPopups.has(popup.id)) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const currentPercent = (scrollTop / scrollHeight) * 100;

      if (currentPercent >= scrollPercent) {
        hasTriggered = true;
        showPopup(popup);
      }
    });
  }

  function setupHoverTrigger(popup, selector) {
    let hasTriggered = false;

    document.addEventListener('mouseenter', (e) => {
      if (hasTriggered || shownPopups.has(popup.id)) return;
      if (e.target.matches && e.target.matches(selector)) {
        hasTriggered = true;
        showPopup(popup);
      }
    }, true);
  }

  function setupClickTrigger(popup, selector) {
    let hasTriggered = false;

    document.addEventListener('click', (e) => {
      if (hasTriggered || shownPopups.has(popup.id)) return;
      if (e.target.matches && e.target.matches(selector)) {
        hasTriggered = true;
        showPopup(popup);
      }
    });
  }

  function handleShopifyEvent(eventName, eventData) {
    popups.forEach(popup => {
      if (shownPopups.has(popup.id)) return;

      const triggers = popup.frontendTriggers || {};
      if (triggers.customEvents && triggers.customEvents.includes(eventName)) {
        showPopup(popup);
      }
    });
  }

  function handleCartEvent(data) {
    const cartEvents = ['cart_viewed', 'product_added_to_cart', 'product_removed_from_cart'];

    popups.forEach(popup => {
      if (shownPopups.has(popup.id)) return;

      const triggers = popup.frontendTriggers || {};
      if (triggers.customEvents) {
        const hasCartTrigger = triggers.customEvents.some(e => cartEvents.includes(e));
        if (hasCartTrigger) {
          showPopup(popup);
        }
      }
    });
  }

  /**
   * Show popup
   */
  function showPopup(popup) {
    ensureGlobalPopupStyles();

    if (shownPopups.has(popup.id)) return;

    // Re-check URL conditions in case URL changed since trigger setup
    const triggers = popup.frontendTriggers || {};
    if (!checkUrlConditions(triggers.urlConditions)) {
      return;
    }

    shownPopups.add(popup.id);

    const overlay = document.createElement('div');
    overlay.id = `persona-overlay-${popup.id}`;
    overlay.style.cssText = 'position:fixed !important;top:0 !important;left:0 !important;width:100% !important;height:100% !important;background:rgba(0,0,0,0.5) !important;z-index:999998 !important;display:flex !important;align-items:center !important;justify-content:center !important;padding:16px !important;box-sizing:border-box !important;';
    overlay.addEventListener('click', () => closePopup(popup.id));

    const container = document.createElement('div');
    container.id = `persona-popup-${popup.id}`;

    const width = popup.popupWidth || '600px';
    const height = popup.popupHeight || 'auto';


    container.style.cssText = `
      position: relative !important;
      z-index: 999999 !important;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3) !important;
      border-radius: 8px !important;
      overflow: visible !important;
      display: block !important;
      background: white !important;
      width: ${width} !important;
      ${height !== 'auto' ? `height: ${height} !important;` : 'min-height: 200px !important;'}
      max-width: 90vw !important;
      max-height: 90vh !important;
      padding: 20px !important;
      margin: 0 auto !important;
    `.replace(/\s+/g, ' ').trim();

    container.innerHTML = popup.renderedHtml;

    const unlayerCloseBtn = container.querySelector('.u-close-button, a[onclick*="u-popup-container"]');
    if (unlayerCloseBtn) {
      unlayerCloseBtn.removeAttribute('onclick');
      unlayerCloseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        closePopup(popup.id);
      });
    } else {
      const closeBtn = document.createElement('button');
      closeBtn.textContent = '√ó';
      closeBtn.style.cssText = 'position:absolute !important;top:10px !important;right:10px !important;border:none !important;background:rgba(255,255,255,0.9) !important;font-size:24px !important;cursor:pointer !important;color:#333 !important;width:30px !important;height:30px !important;border-radius:50% !important;display:flex !important;align-items:center !important;justify-content:center !important;z-index:10 !important;';
      closeBtn.addEventListener('click', () => closePopup(popup.id));
      container.insertBefore(closeBtn, container.firstChild);
    }

    container.addEventListener('click', (e) => e.stopPropagation());

    overlay.appendChild(container);
    document.body.appendChild(overlay);

    // Handle clicks on .u-popup-container (from Unlayer HTML) to close popup
    const unlayerContainers = container.querySelectorAll('.u-popup-container');
    unlayerContainers.forEach(unlayerContainer => {
      unlayerContainer.addEventListener('click', (e) => {
        // Only close if clicking on the container itself, not its children
        if (e.target === unlayerContainer) {
          closePopup(popup.id);
        }
      });
    });

    const scripts = container.querySelectorAll('script');
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    const viewsObj = getObjectCookie(COOKIE_KEY_POPUP_VIEWS);
    viewsObj[popup.id] = (viewsObj[popup.id] || 0) + 1;
    setObjectCookie(COOKIE_KEY_POPUP_VIEWS, viewsObj);


    setupFormHandlers(container, popup.id);
  }

  function closePopup(popupId) {
    const container = document.getElementById(`persona-popup-${popupId}`);
    const overlay = document.getElementById(`persona-overlay-${popupId}`);
    if (container) container.remove();
    if (overlay) overlay.remove();
  }

  /**
   * Setup form submission handlers
   */
  function setupFormHandlers(container, popupId) {
    const forms = container.querySelectorAll('form');

    forms.forEach((form, index) => {


      form.addEventListener('popup-form-success', async (e) => {

        const response = e.detail;
        if (response?.upsertedResources) {
          const contactResource = response.upsertedResources.find(
            r => r.resourceType === 'CONTACT' && r.hash
          );
          if (contactResource?.hash) {
            setCookie(COOKIE_KEY_CUSTOMER_HASH, contactResource.hash);
            // Popups that need a known contact become eligible now
            reloadPopupsIfHashChanged();
          }
        }

        setTimeout(() => {
          closePopup(popupId);
        }, 5000);
      });

      form.addEventListener('popup-form-error', (e) => {
        console.error('[Persona DOM] Form error:', e.detail);
        showErrorMessage('Sorry, there was an error. Please try again.');
      });

      form.addEventListener('landing-page-form-success', async (e) => {

        const response = e.detail;
        if (response?.upsertedResources) {
          const contactResource = response.upsertedResources.find(
            r => r.resourceType === 'CONTACT' && r.hash
          );
          if (contactResource?.hash) {
            setCookie(COOKIE_KEY_CUSTOMER_HASH, contactResource.hash);
          }
        }
      });

      form.addEventListener('landing-page-form-error', (e) => {
        console.error('[Persona DOM] Landing page form error:', e.detail);
      });
    });
  }

  function showSuccessMessage(message) {
    const msg = document.createElement('div');
    msg.textContent = message;
    msg.style.cssText = 'position:fixed;top:20px;right:20px;background:#4caf50;color:white;padding:15px 20px;border-radius:4px;z-index:1000000;box-shadow:0 2px 10px rgba(0,0,0,0.2);';
    document.body.appendChild(msg);
    setTimeout(() => msg.remove(), 3000);
  }

  function showErrorMessage(message) {
    const msg = document.createElement('div');
    msg.textContent = message;
    msg.style.cssText = 'position:fixed;top:20px;right:20px;background:#f44336;color:white;padding:15px 20px;border-radius:4px;z-index:1000000;box-shadow:0 2px 10px rgba(0,0,0,0.2);';
    document.body.appendChild(msg);
    setTimeout(() => msg.remove(), 3000);
  }

  /**
   * Poll for cart sync requests from sandbox pixel
   * Sandbox can't use postMessage in Shopify, so we use cookies
   */
  let lastSyncRequest = 0;

  setInterval(() => {
    const syncRequest = getCookie('persona_sync_cart_request');
    if (!syncRequest) return;

    const requestTimestamp = parseInt(syncRequest);
    if (requestTimestamp <= lastSyncRequest) return;

    lastSyncRequest = requestTimestamp;

    handleCartSyncRequest();
  }, 500);

  /**
   * Fetch Shopify cart and send to sandbox via cookie
   */
  async function handleCartSyncRequest() {
    try {
      const response = await fetch('/cart.json');
      if (!response.ok) {
        setCookie('persona_cart_sync_status', 'error');
        return;
      }

      const shopifyCart = await response.json();

      const cartItems = (shopifyCart.items || []).map(item => ({
        variantId: String(item.variant_id || item.id),
        quantity: item.quantity,
        sku: item.sku,
        price: item.final_price,
        title: item.product_title,
        variant: item.variant_title,
        image: item.image,
        date: new Date().toISOString()
      }));

      setCookie('persona_cart_data', JSON.stringify(cartItems));
      setCookie('persona_cart_sync_response', Date.now().toString());
      setCookie('persona_cart_sync_status', 'success');
    } catch (error) {
      console.error('[Persona DOM] Cart sync failed:', error);
      setCookie('persona_cart_sync_status', 'error');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadPopups);
  } else {
    loadPopups();
  }

})();
